import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { toneClass, type DsTone } from '@/ds/tone'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons, type DsIcon } from '@/ds/icons'

export type DsListDensity = 'compact' | 'comfortable'

export interface DsListItemProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode
  subtitle?: ReactNode
  meta?: ReactNode
  leading?: ReactNode
  trailing?: ReactNode
  onClick?: () => void
  selected?: boolean
  disabled?: boolean
  showChevron?: boolean
  density?: DsListDensity
  destructive?: boolean
  semanticLabel?: string
}

export const DsListItem = forwardRef<HTMLDivElement, DsListItemProps>(function DsListItem(
  { title, subtitle, meta, leading, trailing, onClick, selected, disabled, showChevron, density = 'comfortable', destructive, semanticLabel, className, ...rest },
  ref,
) {
  const interactive = !!onClick && !disabled
  return (
    <div
      ref={ref}
      role={onClick ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={semanticLabel}
      aria-disabled={disabled || undefined}
      aria-current={selected ? 'true' : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={(e) => interactive && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onClick?.())}
      className={cn(
        'flex w-full items-center gap-3 px-4 text-left outline-none transition-colors motion-fast',
        density === 'compact' ? 'min-h-8 py-2' : 'min-h-10 py-3',
        interactive && 'cursor-pointer hover:bg-muted focus-visible:ring-focus focus-visible:relative focus-visible:z-10',
        selected && 'bg-accent',
        disabled && 'opacity-50',
        className,
      )}
      {...rest}
    >
      {leading}
      <div className="flex min-w-0 flex-1 flex-col">
        {title && <span className={cn('truncate text-body-strong', destructive ? 'text-danger-soft-foreground' : 'text-foreground')}>{title}</span>}
        {subtitle && <span className="line-clamp-2 text-small text-muted-foreground">{subtitle}</span>}
        {meta && <span className="text-caption text-muted-foreground">{meta}</span>}
      </div>
      {trailing && <div className="flex shrink-0 items-center gap-2">{trailing}</div>}
      {showChevron && <DsIconGlyph icon={DsIcons.chevronRight} size={16} muted />}
    </div>
  )
})

export function DsIconTile({ icon, size = 40, tone = 'neutral', className }: { icon: DsIcon; size?: number; tone?: DsTone; className?: string }) {
  const t = toneClass[tone]
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center rounded-lg', t.soft, t.onSoft, className)} style={{ width: size, height: size }}>
      <DsIconGlyph icon={icon} size={Math.round(size * 0.5)} />
    </span>
  )
}

export function DsListGroup({ children, className, title }: { children: ReactNode; className?: string; title?: string }) {
  return (
    <div className={cn('flex flex-col', className)}>
      {title && <span className="mb-2 px-1 text-overline uppercase text-muted-foreground">{title}</span>}
      <div className="overflow-hidden rounded-xl border bg-card [&>*+*]:border-t">{children}</div>
    </div>
  )
}
